import { Link } from 'react-router-dom'
import { CalendarClock, Receipt, ShieldCheck, Shirt } from 'lucide-react'
import { baht, isEventOpen, minPriceOf, thaiDate } from '@jaikrob/shared'
import { useEventConfig } from '@/hooks/useEvent'
import { Badge, Button, Card, Skeleton } from '@/components/ui/primitives'
import { PageShell, StateMessage } from '@/components/Layout'

export const HomePage = () => {
  const event = useEventConfig()

  if (event.state === 'loading') {
    return (
      <PageShell title="กำลังโหลด…">
        <div className="flex flex-col gap-4">
          <Skeleton className="aspect-[4/3] w-full rounded-lg" />
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-tap w-full" />
        </div>
      </PageShell>
    )
  }

  if (event.state === 'error') {
    return (
      <PageShell title="จ่ายครบ">
        <StateMessage tone="error" title="โหลดข้อมูลงานไม่สำเร็จ" detail={event.message} />
      </PageShell>
    )
  }

  const config = event.data
  const open = isEventOpen(config, new Date())

  return (
    <PageShell title={config.title}>
      <div className="flex flex-col gap-4">
        <div className="relative overflow-hidden rounded-lg border border-stone-200 bg-white">
          {config.coverImage ? (
            <img
              src={config.coverImage}
              alt={config.title}
              className="aspect-[4/3] w-full object-cover"
              loading="eager"
            />
          ) : (
            <div className="grid aspect-[4/3] w-full place-items-center bg-stone-100 text-stone-300">
              <Shirt className="size-16" aria-hidden />
            </div>
          )}
          <div className="absolute left-3 top-3 flex gap-2">
            <Badge tone="brand">พรีออเดอร์</Badge>
            {open ? null : <Badge tone="danger">ปิดรับแล้ว</Badge>}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-bold leading-snug">{config.title}</h2>
          {config.subtitle ? <p className="text-stone-600">{config.subtitle}</p> : null}
        </div>

        <Card className="flex flex-col divide-y divide-stone-100">
          <div className="flex items-center gap-3 p-3">
            <Receipt className="size-5 shrink-0 text-brand" aria-hidden />
            <span className="text-sm text-stone-600">ราคาเริ่มต้น</span>
            <span className="ml-auto font-semibold">{baht(minPriceOf(config))}</span>
          </div>
          <div className="flex items-center gap-3 p-3">
            <CalendarClock className="size-5 shrink-0 text-brand" aria-hidden />
            <span className="text-sm text-stone-600">ปิดรับ</span>
            <span className="ml-auto font-semibold">{thaiDate(config.closeAt)}</span>
          </div>
          {config.shipping.enabled ? (
            <div className="flex items-center gap-3 p-3">
              <ShieldCheck className="size-5 shrink-0 text-brand" aria-hidden />
              <span className="text-sm text-stone-600">จัดส่ง (ถ้าเลือก)</span>
              <span className="ml-auto font-semibold">{baht(config.shipping.fee)} ต่อออเดอร์</span>
            </div>
          ) : null}
        </Card>

        {open ? (
          <Button asChild className="w-full">
            <Link to="/order">สั่งเสื้อ</Link>
          </Button>
        ) : (
          <StateMessage
            tone="warning"
            title="ปิดรับออเดอร์แล้ว"
            detail="ขออภัย งานนี้ปิดรับพรีออเดอร์แล้ว"
          />
        )}

        <Link to="/check" className="text-center text-sm font-medium text-brand underline underline-offset-2">
          เช็กออเดอร์ของฉัน
        </Link>

        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          เป็นการสั่งผลิตล่วงหน้า ไม่มีสต็อก จึง<strong>ไม่สามารถยกเลิกหรือขอคืนเงินได้</strong>ทุกกรณี
        </p>
      </div>
    </PageShell>
  )
}

export default HomePage
